<script setup>
import { ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'next/button/Button.vue';
import ToggleSwitch from 'dashboard/components-next/switch/Switch.vue';
import { BaseTableRow, BaseTableCell } from 'dashboard/components-next/table';
import { messageStamp } from 'shared/helpers/timeHelper';

const props = defineProps({
  task: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['edit', 'delete', 'toggle']);
const { t } = useI18n();

const typeLabel = type => {
  const key = `WGPT_TASKS.TASKS.TYPE.${(type || '').toUpperCase()}`;
  return t(key);
};

const taskEnabled = ref(false);

const onTaskEnabledChange = value => {
  taskEnabled.value = !value;
  emit('toggle', props.task);
};

watch(
  () => props.task.active,
  newVal => {
    taskEnabled.value = newVal;
  },
  { immediate: true }
);

const readableDate = date =>
  messageStamp(new Date(date).getTime() / 1000, 'LLL d, yyyy');
const readableDateWithTime = date =>
  messageStamp(new Date(date).getTime() / 1000, 'LLL d, yyyy hh:mm a');
</script>

<template>
  <BaseTableRow :item="task">
    <template #default>
      <BaseTableCell class="max-w-0 w-full">
        <div class="flex flex-col items-start gap-1">
          <span class="block text-heading-3 text-n-slate-12 capitalize">
            {{ task.name }}
          </span>
          <p class="mb-0 text-n-slate-11 text-body-main line-clamp-2">
            {{ task.description || '-' }}
          </p>
        </div>
      </BaseTableCell>

      <BaseTableCell>
        <span class="text-body-main text-n-slate-11 whitespace-nowrap">
          {{ typeLabel(task.type) }}
        </span>
      </BaseTableCell>

      <BaseTableCell>
        <ToggleSwitch v-model="taskEnabled" @change="onTaskEnabledChange" />
      </BaseTableCell>

      <BaseTableCell :title="readableDateWithTime(task.updated_at)">
        <span class="text-body-main text-n-slate-12 whitespace-nowrap">
          {{ readableDate(task.updated_at) }}
        </span>
      </BaseTableCell>

      <BaseTableCell align="end">
        <div class="flex gap-3 justify-end flex-shrink-0">
          <Button
            v-tooltip.top="t('WGPT_TASKS.TASKS.EDIT')"
            icon="i-woot-edit-pen"
            sm
            slate
            @click="emit('edit', task)"
          />
          <Button
            v-tooltip.top="t('WGPT_TASKS.TASKS.DELETE')"
            icon="i-woot-bin"
            slate
            sm
            class="hover:enabled:text-n-ruby-11 hover:enabled:bg-n-ruby-2"
            @click="emit('delete', task)"
          />
        </div>
      </BaseTableCell>
    </template>
  </BaseTableRow>
</template>
